import { Document, Page, Text, Image, View } from "@react-pdf/renderer";
import {
  orderTypeConstants,
  ED_ORDER_TYPE_EXPIRATION_PERIOD,
} from "constants/order";
import dayjs from "dayjs";
import { parseDate } from "helpers/date";
import { isEmpty } from "lodash";
import { useMemo } from "react";
import { Pricing } from "types/CreateOrder";
import {
  IUniqueProcedure,
  IUniqueProceduresByProvider,
} from "types/Order/Order";
import { SpecialtyList } from "types/Provider/Provider";

import { cellphoneImage } from "./assets/cellphoneImage";
import { detailBgImage } from "./assets/detailBgImage";
import { ChargeDescriptionClearEstimate } from "./partials/ChargeDescription";
import { ChargeDescriptionV3 } from "./partials/ChargeDescriptionV3";
import { EnglishInformation } from "./partials/EnglishInformation";
import { Footer } from "./partials/Footer";
import { Header } from "./partials/Header";
import { NoDiscountPdfBreakdown } from "./partials/NoDiscountPdfBreakdown/NoDiscountPdfBreakdown";
import { PlasticSurgeryText } from "./partials/PlasticSurgeryText";
import { ProcedureBreakdown } from "./partials/ProcedureBreakdown";
import { SpanishInformation } from "./partials/SpanishInformation";
import { V3Disclaimer } from "./partials/V3Disclaimer";
import { styles } from "./styles";

export type Props = {
  name: string;
  facilityLogo: string;
  fullAddress?: string;
  phoneNumber?: string;
  serviceDetails: any;
  dateOfBirth: string;
  payerName: string;
  serviceDate: string;
  facilityName?: string;
  gfeQuote: Pricing | undefined;
  clearPriceToPay: number;
  isGFE: boolean;
  insuranceAmounts: any;
  providerFee: number | undefined;
  facilityFee: number;
  clearDiscount: number;
  gfeNoDiscountPdf?: boolean;
  gfeDisclaimer?: {
    disclaimerEnglish: { title?: string; description?: string };
    disclaimerSpanish: { title?: string; description?: string };
  };
  accountNumber?: string;
  note?: string;
  isVersion3Pdf?: boolean;
  hoursEstimated?: number | null;
  fullProviderBreakdown?: any;
  paymentFullBreakdownByCPTCode?: any;
  clearPatientFeeValueInPercentage?: number | null;
  orderType?: string;
  insuranceBenefits: any;
  paymentBreakdown: any;
};

export function ClearOrderDocument({
  name,
  facilityLogo,
  fullAddress,
  phoneNumber,
  serviceDetails,
  dateOfBirth,
  payerName,
  serviceDate,
  facilityName,
  gfeQuote,
  clearPriceToPay,
  isGFE,
  insuranceAmounts,
  providerFee,
  facilityFee,
  clearDiscount,
  gfeNoDiscountPdf,
  gfeDisclaimer,
  accountNumber,
  note,
  isVersion3Pdf,
  hoursEstimated,
  fullProviderBreakdown,
  paymentFullBreakdownByCPTCode,
  clearPatientFeeValueInPercentage,
  orderType,
  insuranceBenefits,
  paymentBreakdown,
}: Props) {
  const uniqueProcedureList = useMemo(() => {
    if (!serviceDetails || !serviceDetails.procedures) return [];

    const { procedures } = serviceDetails;

    // Use an object to group procedures by 'code' and accumulate totals
    const procedureMap = procedures.reduce(
      (acc: any, curr: any) => {
        if (!acc[curr.code]) {
          acc[curr.code] = {
            code: curr.code,
            cptDescription: curr.cptDescription,
            totalAmountInCents: 0,
          };
        }
        acc[curr.code].totalAmountInCents += curr.amountInCents || 0;
        return acc;
      },
      {} as Record<
        string,
        {
          code: string;
          cptDescription: string;
          totalAmountInCents: number;
        }
      >
    );
    return Object.values(procedureMap) as IUniqueProcedure[];
  }, [serviceDetails]);

  const uniqueProcedureListByProviders = useMemo(() => {
    if (
      !paymentFullBreakdownByCPTCode ||
      !paymentFullBreakdownByCPTCode?.bundledProviderAmounts
    )
      return [];

    const procedures = paymentFullBreakdownByCPTCode?.bundledProviderAmounts;
    const providersMap = procedures.reduce((acc: any, procedure: any) => {
      // If the provider is not yet in the accumulator, add it.
      if (!acc[procedure.providerId]) {
        acc[procedure.providerId] = {
          providerId: procedure.providerId,
          providerName:
            procedure.providerName === "Dr. Jeff Markey"
              ? "Ascentist Plastic Surgery"
              : procedure.providerName,
          specialty: procedure.specialty,
          subtotal: 0,
          procedures: {},
        };
      }
      acc[procedure.providerId].subtotal += procedure.patientPortion;

      const proceduresByProvider = acc[procedure.providerId].procedures;

      // Group by procedureId: if not already added, create an object for it.
      if (!proceduresByProvider[procedure.cptCode]) {
        let { cptDescription } = procedure;
        if (procedure.cptDescription === "Anesthesia/Facility Fee") {
          if (procedure.specialty === SpecialtyList.ANESTHESIOLOGIST) {
            cptDescription = "Anesthesia Fee";
          }
          if (procedure.specialty === SpecialtyList.FACILITY) {
            cptDescription = "Facility Fee";
          }
        }
        proceduresByProvider[procedure.cptCode] = {
          procedureId: procedure.procedureId,
          code: procedure.cptCode,
          cptDescription,
          totalAmountInCents: 0,
        };
      }

      // Add the amount for this occurrence.
      proceduresByProvider[procedure.cptCode].totalAmountInCents +=
        procedure.patientPortion;

      return acc;
    }, {});

    // Convert the map into an array and transform the grouped procedures object into an array.
    return Object.values(providersMap).map((provider: any) => {
      return {
        providerId: provider.providerId,
        providerName: provider.providerName,
        subtotal: provider.subtotal,
        procedures: Object.values(provider.procedures),
      };
    }) as IUniqueProceduresByProvider[];
  }, [serviceDetails]);

  const hasMarkey = useMemo(() => {
    const { procedures } = serviceDetails;
    const hasDrMarley = procedures.find(
      (procedure: any) => procedure?.providerName === "Dr. Jeff Markey"
    );
    return !!hasDrMarley;
  }, [serviceDetails]);

  const expirationDate = useMemo(() => {
    const today = new Date();
    const sixWeeksFromToday = new Date(
      today.getTime() + 6 * 7 * 24 * 60 * 60 * 1000
    );
    return parseDate(sixWeeksFromToday);
  }, []);

  const today = useMemo(() => {
    const todayDate = new Date();
    return parseDate(todayDate);
  }, []);

  const showPromptPayDiscount = useMemo(() => {
    if (isGFE && gfeNoDiscountPdf) return false;
    return true;
  }, [isGFE, gfeNoDiscountPdf]);

  const expirationDays = useMemo(() => {
    if (
      orderType === orderTypeConstants.ED ||
      orderType === orderTypeConstants.BARIATRICS
    ) {
      return ED_ORDER_TYPE_EXPIRATION_PERIOD;
    }
    return 0;
  }, [orderType]);

  const exparel = useMemo(() => {
    const exparelProceduresAmount = serviceDetails.procedures
      .filter((p: any) => p?.code === "Exparel")
      .reduce((acc: number, current: any) => {
        return acc + (current?.amountInCents || 0);
      }, 0);
    return exparelProceduresAmount || 0;
  }, [serviceDetails]);

  const scd = useMemo(() => {
    const scdProceduresAmount = serviceDetails.procedures
      .filter((p: any) => p?.code === "SCD")
      .reduce((acc: number, current: any) => {
        return acc + (current?.amountInCents || 0);
      }, 0);
    return scdProceduresAmount || 0;
  }, [serviceDetails]);

  const surgeonFees = useMemo(() => {
    const sumOfSurgeonFees = fullProviderBreakdown
      ?.filter((p: any) => p?.specialty === SpecialtyList.PRIMARY_PROVIDER)
      .reduce((acc: number, current: any) => {
        return acc + (current?.clearDiscountedPrice || 0);
      }, 0);
    return sumOfSurgeonFees || 0;
  }, [fullProviderBreakdown]);

  const anesthesiaFees = useMemo(() => {
    const sumOfAnesthesiaFees = fullProviderBreakdown
      ?.filter((p: any) => p?.specialty === SpecialtyList.ANESTHESIOLOGIST)
      .reduce((acc: number, current: any) => {
        return acc + (current?.clearDiscountedPrice || 0);
      }, 0);
    return sumOfAnesthesiaFees || 0;
  }, [fullProviderBreakdown]);

  const facilityFees = useMemo(() => {
    const sumOfFacilityFees = fullProviderBreakdown
      ?.filter((p: any) => p?.specialty === SpecialtyList.FACILITY)
      .reduce((acc: number, current: any) => {
        return acc + (current?.clearDiscountedPrice || 0);
      }, 0);
    return sumOfFacilityFees - scd - exparel;
  }, [fullProviderBreakdown, scd, exparel]);

  return (
    <Document>
      <Page size="LETTER" style={{ ...styles.page, position: "relative" }}>
        <Header
          facilityLogo={facilityLogo}
          fullAddress={fullAddress}
          phoneNumber={phoneNumber}
          showAddress={!hasMarkey}
        />
        {/* CONTENT */}
        <View style={styles.content} wrap>
          <View style={{ ...styles.section, marginTop: 0 }}>
            <Text
              style={{
                ...styles.title,
                ...(hasMarkey ? { color: "#234990", fontSize: "13px" } : {}),
              }}
            >
              {hasMarkey
                ? "Cosmetic Procedure Agreement"
                : "Good Faith Estimate"}
            </Text>
          </View>
          <View style={{ ...styles.section, marginTop: 0 }}>
            <Text style={{ ...styles.bodyText, lineHeight: 1.2 }}>
              {hasMarkey ? (
                <Text>
                  Thank you for your interest in facial plastic surgery with{" "}
                  <Text style={{ fontFamily: "Helvetica-Bold" }}>
                    Dr. Markey
                  </Text>{" "}
                  and{" "}
                  <Text style={{ fontFamily: "Helvetica-Bold" }}>
                    Ascentist Plastic Surgery.
                  </Text>{" "}
                  This letter is to provide you with estimated cosmetic fees
                  regarding the procedure(s) discussed. Please note, the
                  anesthesia fees are calculated by the length of the procedure.
                  The actual price may differ from the estimate below in
                  accordance with the final operative time.`
                </Text>
              ) : (
                `This estimate uses procedure information provided by your physician as well as the benefits available from your insurance company (if applicable). This is not a guarantee of the final amount due. Therefore, the final amount owed may vary based on circumstances involved in your actual services.`
              )}
            </Text>
            {!isVersion3Pdf && !hasMarkey && (
              <Text
                style={{ ...styles.bodyText, lineHeight: 1.2, marginBottom: 5 }}
              >
                Esta estimación utiliza la información del procedimiento
                proporcionada por su médico, así como los beneficios disponibles
                de su compañía de seguros (si corresponde). Esto no es una
                garantía del monto final adeudado. Por lo tanto, el monto final
                adeudado puede variar según las circunstancias involucradas en
                sus servicios reales.
              </Text>
            )}
            {hasMarkey && (
              <>
                <Text style={{ ...styles.bodyText, lineHeight: 1.2 }}>
                  These fees include: cosmetic surgical costs and cosmetic
                  anesthesia fees, and all pertinent post-operative visits and
                  management. This quote does not include any functional,
                  medically necessary surgical fees that would be filed to your
                  insurance company. A separate estimate may be provided for the
                  functional portion if requested.
                </Text>
                <Text style={{ ...styles.bodyText, lineHeight: 1.2 }}>
                  <Text style={{ fontFamily: "Helvetica-Bold" }}>
                    Ascentist Plastic Surgery
                  </Text>{" "}
                  {`requires a ${
                    clearPatientFeeValueInPercentage
                      ? "$1,500+ any processing and services fees"
                      : "a $1,500"
                  } deposit to secure the date of your procedure. The remaining balance, if applicable, is to be submitted two weeks prior to the procedure date or before.`}
                </Text>
              </>
            )}
          </View>
          {hasMarkey && (
            <View
              style={{
                marginTop: 0,
                marginBottom: 0,
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between",
                paddingLeft: 50,
                paddingRight: 10,
              }}
            >
              <View
                style={{
                  display: "flex",
                  marginBottom: "8px",
                  flexDirection: "column",
                }}
              >
                <View
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                  }}
                >
                  <Text
                    style={{ ...styles.listTitle, width: 75, fontSize: "10px" }}
                  >
                    Patient:
                  </Text>
                  <Text style={{ fontSize: "10px" }}>{name}</Text>
                </View>
                <View
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                  }}
                >
                  <Text
                    style={{ ...styles.listTitle, width: 75, fontSize: "10px" }}
                  >
                    Date of Birth:
                  </Text>
                  <Text style={{ fontSize: "10px" }}>
                    {parseDate(dateOfBirth)}
                  </Text>
                </View>
              </View>
              <View style={styles.dateWrapper}>
                <View style={styles.dateContainer}>
                  <Text style={styles.dateTitle}>PREPARED DATE</Text>
                  <Text style={styles.dateText}>{today}</Text>
                </View>
                <View style={styles.dateContainer}>
                  <Text style={styles.dateTitle}>EXPIRATION DATE</Text>
                  <Text style={styles.dateText}>{expirationDate}</Text>
                </View>
              </View>
            </View>
          )}
          {!hasMarkey && (
            <View
              style={{
                marginLeft: 50,
                marginRight: 50,
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
                gap: 20,
              }}
            >
              <View style={{ marginTop: 0, marginBottom: 0, width: "50%" }}>
                <View
                  style={{
                    display: "flex",
                    marginBottom: "3px",
                    flexDirection: "row",
                    alignItems: "center",
                  }}
                >
                  <Text style={{ ...styles.listTitle, width: 100 }}>
                    Patient
                  </Text>
                  <Text style={styles.smallText}>{`${name}`}</Text>
                </View>
                <View
                  style={{
                    display: "flex",
                    marginBottom: "3px",
                    flexDirection: "row",
                    alignItems: "center",
                  }}
                >
                  <Text style={{ ...styles.listTitle, width: 100 }}>
                    Date of Birth
                  </Text>
                  <Text style={styles.smallText}>{parseDate(dateOfBirth)}</Text>
                </View>
                {!isEmpty(payerName) && (
                  <View
                    style={{
                      display: "flex",
                      marginBottom: "3px",
                      flexDirection: "row",
                      alignItems: "center",
                    }}
                  >
                    <Text style={{ ...styles.listTitle, width: 100 }}>
                      Payer
                    </Text>
                    <Text style={styles.smallText}>{`${payerName}`}</Text>
                  </View>
                )}
                <View
                  style={{
                    display: "flex",
                    marginBottom: "3px",
                    flexDirection: "row",
                    alignItems: "center",
                  }}
                >
                  <Text style={{ ...styles.listTitle, width: 100 }}>
                    Service Date
                  </Text>
                  <Text style={styles.smallText}>{`${parseDate(
                    serviceDate
                  )}`}</Text>
                </View>
                {isGFE && (
                  <View
                    style={{
                      display: "flex",
                      marginBottom: "3px",
                      flexDirection: "row",
                      alignItems: "center",
                    }}
                  >
                    <Text style={{ ...styles.listTitle, width: 100 }}>
                      Account number
                    </Text>
                    <Text style={styles.smallText}>{accountNumber}</Text>
                  </View>
                )}
              </View>
              <View
                style={{
                  marginTop: 0,
                  marginBottom: 0,
                  display: "flex",
                  flexDirection: "column",
                  width: "50%",
                }}
              >
                <Text style={{ ...styles.listTitle, marginBottom: 4 }}>
                  Services Provided
                </Text>
                {uniqueProcedureList.map((currentProcedure) => (
                  <Text
                    style={{ ...styles.itemList, marginBottom: 3 }}
                    key={currentProcedure.code}
                  >
                    {currentProcedure.cptDescription} - {currentProcedure.code}
                  </Text>
                ))}
              </View>
            </View>
          )}
          {hasMarkey && (
            <View style={{ ...styles.section, marginBottom: 4, marginTop: 0 }}>
              <View style={styles.list}>
                <ProcedureBreakdown
                  proceduresByProviders={uniqueProcedureListByProviders}
                  clearPatientFeeValueInPercentage={
                    clearPatientFeeValueInPercentage
                  }
                />
              </View>
            </View>
          )}
          {isVersion3Pdf && !hasMarkey && (
            <ChargeDescriptionV3
              surgeonFees={surgeonFees}
              anesthesiaFees={anesthesiaFees}
              facilityFees={facilityFees}
              scd={scd}
              exparel={exparel}
              clearPriceToPay={clearPriceToPay}
              proceduresByProviders={uniqueProcedureListByProviders}
              clearPatientFeeValueInPercentage={
                clearPatientFeeValueInPercentage
              }
            />
          )}
          {!isVersion3Pdf && !hasMarkey && showPromptPayDiscount && (
            <ChargeDescriptionClearEstimate
              gfeQuote={gfeQuote}
              clearPriceToPay={clearPriceToPay}
              isGFE={isGFE}
              insuranceAmounts={insuranceAmounts}
              facilityFee={facilityFee}
              providerFee={providerFee}
              clearDiscount={clearDiscount.toString()}
              showPromptPayDiscount={showPromptPayDiscount}
              payerName={payerName}
              clearPatientFeeValueInPercentage={
                clearPatientFeeValueInPercentage
              }
            />
          )}
          {!isVersion3Pdf && !hasMarkey && !showPromptPayDiscount && (
            <NoDiscountPdfBreakdown
              insuranceBenefits={insuranceBenefits}
              paymentBreakdown={paymentBreakdown}
              serviceDetails={serviceDetails}
              orderType={orderType}
              clearPrice={clearPriceToPay}
            />
          )}
          {showPromptPayDiscount && !hasMarkey && !isVersion3Pdf && (
            <View
              style={{
                width: "100%",
                height: 270,
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "center",
                position: "relative",
              }}
              wrap={false}
              minPresenceAhead={270}
            >
              <View
                style={{
                  width: "100%",
                  backgroundColor: "#f0f0f0",
                  paddingTop: 10,
                  paddingBottom: 10,
                  alignItems: "flex-start",
                }}
              >
                <View
                  style={{
                    marginLeft: 50,
                    marginRight: 50,
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "space-between",
                    position: "relative",
                  }}
                >
                  <View
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      width: "50%",
                      textAlign: "left",
                    }}
                  >
                    <Text
                      style={{
                        ...styles.bodyText,
                        marginBottom: 0,
                        lineHeight: "1.4px",
                      }}
                    >
                      To qualify for the{" "}
                      <Text style={{ fontFamily: "Helvetica-Bold" }}>
                        Prompt Pay Discount
                      </Text>{" "}
                      {`you need to pay before
                      ${dayjs
                        .utc(serviceDate)
                        .add(expirationDays, "day")
                        .format("MMMM D, YYYY")}}
                        using the link provided to you via text through our Clear Health Partner.`}
                    </Text>
                    <Text
                      style={{
                        ...styles.bodyText,
                        lineHeight: "1.4px",
                        marginBottom: 4,
                      }}
                    >
                      For more information, please contact Clear Health at
                      1-888-918-2522.
                    </Text>
                    <Text style={styles.bodyText}>
                      {`For questions regarding your estimate please call 1-${phoneNumber}`}
                    </Text>
                  </View>
                </View>
              </View>
              <View
                style={{
                  position: "absolute",
                  right: 20,
                  top: 0,
                }}
              >
                <Image
                  style={{ height: 270, width: 200 }}
                  src={cellphoneImage}
                />
              </View>
            </View>
          )}
          {!showPromptPayDiscount && !isVersion3Pdf && (
            <View style={styles.content}>
              <View style={{ ...styles.section, marginTop: 5 }}>
                <Text
                  style={{
                    ...styles.bodyText,
                    lineHeight: 1.2,
                    textAlign: "justify",
                  }}
                >
                  {note || ""}
                </Text>
              </View>
            </View>
          )}
          {isVersion3Pdf && (
            <View
              style={{
                display: "flex",
                flexDirection: "column",
                textAlign: "justify",
              }}
            >
              <View style={{ ...styles.section, marginTop: 20 }}>
                <Text
                  style={{
                    ...styles.bodyText,
                    lineHeight: 1.2,
                    textAlign: "justify",
                  }}
                >
                  {note ? (
                    <>
                      <Text
                        style={{
                          ...styles.bodyTextBold,
                          lineHeight: 1.2,
                          textAlign: "justify",
                          fontFamily: "Helvetica-Bold",
                        }}
                      >
                        Note added from office:
                      </Text>{" "}
                      {note}{" "}
                    </>
                  ) : (
                    ""
                  )}
                </Text>
              </View>
            </View>
          )}
          {isVersion3Pdf && !hasMarkey && (
            <V3Disclaimer hoursEstimated={hoursEstimated} />
          )}
        </View>
        {(!showPromptPayDiscount || isVersion3Pdf) && !hasMarkey && (
          <View
            style={{
              marginTop: 40,
              borderTop: "1px solid black",
              width: "50%",
              alignSelf: "center",
              position: "absolute",
              bottom: 40,
            }}
          >
            <Text
              style={{
                ...styles.bodyText,
                textAlign: "center",
                marginTop: 4,
              }}
            >
              Sign here
            </Text>
          </View>
        )}
        {/* END OF CONTENT */}
        <Footer facilityName={facilityName} />
      </Page>
      {/* {!isVersion3Pdf && !hasMarkey && showPromptPayDiscount && (
        <EnglishInformation
          facilityLogo={facilityLogo}
          fullAddress={fullAddress || ""}
          phoneNumber={phoneNumber || ""}
          facilityName={facilityName || ""}
          isGFE={isGFE}
          gfeDisclaimer={gfeDisclaimer?.disclaimerEnglish}
        />
      )}
      {!isVersion3Pdf && !hasMarkey && showPromptPayDiscount && (
        <SpanishInformation
          facilityLogo={facilityLogo}
          fullAddress={fullAddress || ""}
          phoneNumber={phoneNumber || ""}
          facilityName={facilityName || ""}
          isGFE={isGFE}
          gfeDisclaimer={gfeDisclaimer?.disclaimerSpanish}
        />
      )} */}
      {hasMarkey && (
        <PlasticSurgeryText
          facilityLogo={facilityLogo}
          fullAddress={fullAddress || ""}
          phoneNumber={phoneNumber || ""}
          facilityName={facilityName || ""}
          showAddress={false}
        />
      )}
    </Document>
  );
}
